import { DesignSystemTypography, TextVariant } from "@/components/typography/design-system";
import { classNames } from "@/utils/classnames";
import React, { ReactNode } from "react";

// ------------------------------------------------------------------
// FieldLabelV2 — label + optional description block that sits above a
// form control (InputV2, SelectV2, future Radio/Checkbox, …).
//
// Semantically a plain HTML <label>, so clicking it focuses the
// associated control when `htmlFor` is wired up. The description is
// rendered as a sibling <p> with a stable id, intended to be listed
// in the control's `aria-describedby`.
// ------------------------------------------------------------------

export const FieldLabelV2Sizes = ["sm", "md"] as const;
export type FieldLabelV2Size = (typeof FieldLabelV2Sizes)[number];

// Dashboard DS typography scale — resolves to CSS vars defined on
// the `.design-system-typography` ancestor applied by Layout.
const labelTextVariant: Record<FieldLabelV2Size, TextVariant> = {
  sm: "sm",
  md: "md",
};

// Description sits one DS step smaller than the label — sm → 11px
// (cds-xs), md → 12px (cds-sm).
const descriptionTextVariant: Record<FieldLabelV2Size, TextVariant> = {
  sm: "xs",
  md: "sm",
};

// Inner gap between label row and description line.
const labelBlockGap: Record<FieldLabelV2Size, string> = {
  sm: "gap-cds-2",
  md: "gap-cds-4",
};

export interface FieldLabelV2Props {
  /** Main label text. */
  label: string;
  /** When true, a red `*` is appended. Callers should also set the
   *  paired control's `aria-required`. */
  required?: boolean;
  /** Helper text rendered below the label. */
  description?: string;
  /** Matches the paired control's size (keeps typography proportions
   *  consistent). */
  size?: FieldLabelV2Size;
  /** Associates the <label> with a form control via `for`. When present,
   *  clicking the label focuses the control. */
  htmlFor?: string;
  /** Id for the description element — the paired control should list
   *  this in its `aria-describedby` so screen readers announce the
   *  helper text alongside the label. */
  descriptionId?: string;
  /** Optional compact action rendered inline after the label text. */
  trailingAction?: ReactNode;
  /** Visually hide the label while keeping it in the accessibility tree
   *  (`sr-only`). For inline/compact controls — e.g. an inline rename —
   *  that can't show a label but still need an accessible name. Skips the
   *  padded wrapper and the description so the field takes no extra space. */
  hidden?: boolean;
}

export const FieldLabelV2: React.FC<FieldLabelV2Props> = ({
  label,
  required = false,
  description,
  size = "md",
  htmlFor,
  descriptionId,
  trailingAction,
  hidden = false,
}) => {
  if (hidden) {
    // `sr-only`: read by screen readers, no visual footprint (the padded
    // wrapper and description are intentionally dropped in this mode).
    return (
      <label htmlFor={htmlFor} className={"sr-only"}>
        {label}
      </label>
    );
  }

  return (
    <div className={classNames("flex flex-col pb-cds-4", labelBlockGap[size])}>
      <div className={"flex min-w-0 items-center gap-cds-2"}>
        <label
          htmlFor={htmlFor}
          className={classNames(
            // `!` beats `dark-mode.css`'s `.dark label { color: zinc-300 }`
            // rule (class+element specificity > plain class), which would
            // otherwise paint our zinc-800 label as zinc-300 in any
            // subtree under `html.dark` — including the `.theme-light`
            // preview section.
            "cursor-default font-medium !text-cds-ink dark:!text-zinc-200",
            DesignSystemTypography[labelTextVariant[size]],
          )}
        >
          {label}
          {required && (
            <span className={"text-cds-error"} aria-hidden>
              *
            </span>
          )}
        </label>
        {trailingAction != null && (
          <div className={"relative flex h-0 shrink-0 items-center overflow-visible"}>
            {trailingAction}
          </div>
        )}
      </div>
      {description && (
        <p
          id={descriptionId}
          // Subtitle: zinc-400 in light, zinc-500 in dark (one shade
          // lighter than the strict rotation would give). `!` beats
          // body-color inheritance and legacy `.dark` overrides that
          // might otherwise repaint the <p>. `cursor-default` so
          // hovering doesn't flip to a text-insertion caret — keeps
          // the subtitle feeling like a label, not editable text.
          className={classNames(
            "!text-cds-ink-muted dark:!text-zinc-500 cursor-default",
            DesignSystemTypography[descriptionTextVariant[size]],
          )}
        >
          {description}
        </p>
      )}
    </div>
  );
};
